/**
 * Header behaviour.
 *
 *  <sticky-header>  stuck / hidden state and the scroll progress bar. The tag name is kept
 *                   because cart-notification.js, media-gallery.js and quick-order-list.js
 *                   look the header up with document.querySelector('sticky-header').
 *  <kvs-nav>        pill navigation panels (hover, keyboard focus, touch, Escape).
 *  <kvs-announcements>  rotating announcement messages.
 */

if (!customElements.get('sticky-header')) {
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.type = this.dataset.stickyType || 'always';
      this.wrapper = this.closest('.section-header');
      this.progressBar = this.querySelector('.kvs-header__progress-bar');
      this.lastY = window.scrollY;
      this.frame = 0;
      this.preventReveal = false;

      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.addEventListener('preventHeaderReveal', () => {
        this.preventReveal = true;
      });

      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      cancelAnimationFrame(this.frame);
    }

    onScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    }

    update() {
      const y = Math.max(window.scrollY, 0);
      const isSticky = this.type !== 'none' && this.wrapper;
      const stickyTop = isSticky ? parseFloat(getComputedStyle(this.wrapper).top) || 0 : 0;
      const stuck = Boolean(isSticky) && y > 0 && this.wrapper.getBoundingClientRect().top <= stickyTop + 1;

      this.classList.toggle('is-stuck', stuck);

      if (this.type === 'on-scroll-up') {
        const delta = y - this.lastY;
        if (stuck && delta > 6 && y > this.offsetHeight && !this.mustStayVisible()) {
          this.classList.add('is-hidden');
        } else if (delta < -6 || !stuck) {
          if (this.preventReveal && stuck) {
            clearTimeout(this.preventTimer);
            this.preventTimer = setTimeout(() => {
              this.preventReveal = false;
            }, 150);
          } else {
            this.reveal();
          }
        }
      }

      this.lastY = y;
      this.updateProgress(y);
    }

    updateProgress(y) {
      if (!this.progressBar) return;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const ratio = max > 0 ? Math.min(y / max, 1) : 0;
      this.progressBar.style.transform = `scaleX(${ratio.toFixed(4)})`;
    }

    mustStayVisible() {
      return (
        Boolean(this.querySelector(':focus-visible')) ||
        this.hasAttribute('data-prevent-hide') ||
        Boolean(this.querySelector('.kvs-nav__item.is-open'))
      );
    }

    /** Public: also called by cart-notification.js after an add to cart. */
    reveal() {
      this.classList.remove('is-hidden');
    }
  }

  customElements.define('sticky-header', StickyHeader);
}

if (!customElements.get('kvs-nav')) {
  class KvsNav extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.kvs-nav__item[data-has-panel]'));
      this.timers = new Map();
      this.lastPointer = 'mouse';
      this.onTravel = this.onTravel.bind(this);

      this.addEventListener('pointerdown', (event) => (this.lastPointer = event.pointerType), true);
      this.addEventListener('keydown', (event) => {
        this.lastPointer = 'keyboard';
        if (event.key === 'Escape') this.closeAll(true);
      });

      this.onOutsidePointer = (event) => {
        if (!this.contains(event.target)) this.closeAll();
      };
      document.addEventListener('pointerdown', this.onOutsidePointer);

      this.items.forEach((item) => this.bind(item));
    }

    disconnectedCallback() {
      document.removeEventListener('pointerdown', this.onOutsidePointer);
      this.stopTravel();
    }

    bind(item) {
      const link = item.querySelector(':scope > .kvs-nav__link');
      const panel = item.querySelector(':scope > .kvs-nav__panel');

      item.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') this.open(item);
      });
      // A short grace period, and none at all while the pointer travels on towards the panel (see onTravel).
      item.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse' || item.contains(event.relatedTarget)) return;
        this.close(item, 350);
        this.stopTravel();
        this.travel = { item, armed: true };
        document.addEventListener('pointermove', this.onTravel, { passive: true });
      });
      panel.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') this.open(item);
      });
      item.addEventListener('focusin', () => {
        if (!this.suppressFocusOpen && (this.lastPointer !== 'touch' || link.matches(':focus-visible'))) {
          this.open(item);
        }
      });
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) this.close(item);
      });

      // Touch: the first tap opens the panel, the second follows the link.
      link.addEventListener('click', (event) => {
        if (this.lastPointer === 'touch' && !item.classList.contains('is-open')) {
          event.preventDefault();
          this.open(item);
        }
      });
    }

    /*
     * The pointer left an open item. However slowly or diagonally it crosses the bar, also over the neighbouring
     * pills, the panel stays while the pointer is in the band between the top of the pill and the top of the panel.
     * A brief pause on a neighbouring link is still part of the trip. A longer rest there closes the panel;
     * leaving the band keeps the short close delay.
     */
    onTravel(event) {
      const { item } = this.travel || {};
      if (!item || !item.classList.contains('is-open')) {
        this.stopTravel();
        return;
      }
      if (item.contains(event.target)) {
        clearTimeout(this.timers.get(item));
        this.stopTravel();
        return;
      }
      const pill = item.querySelector(':scope > .kvs-nav__link').getBoundingClientRect();
      const panel = item.querySelector(':scope > .kvs-nav__panel').getBoundingClientRect();
      const inBand =
        event.clientY >= pill.top &&
        event.clientY <= panel.top + 1 &&
        event.clientX >= Math.min(pill.left, panel.left) &&
        event.clientX <= Math.max(pill.right, panel.right);
      const onOtherLink = event.target instanceof Element && Boolean(event.target.closest('a, button, summary'));

      if (inBand && !onOtherLink) {
        clearTimeout(this.timers.get(item));
        this.travel.armed = false;
      } else if (inBand) {
        // Allow a short hesitation over a neighbouring link before the visitor enters the panel.
        this.close(item, 800);
        this.travel.armed = true;
      } else if (!this.travel.armed) {
        this.close(item, 350);
        this.travel.armed = true;
      }
    }

    stopTravel() {
      document.removeEventListener('pointermove', this.onTravel);
      this.travel = null;
    }

    open(item) {
      clearTimeout(this.timers.get(item));
      if (this.travel?.item === item) this.stopTravel();
      this.items.forEach((other) => other !== item && this.close(other));
      item.classList.add('is-open');
      item.querySelector(':scope > .kvs-nav__link').setAttribute('aria-expanded', 'true');
    }

    close(item, delay = 0) {
      clearTimeout(this.timers.get(item));
      const run = () => {
        if (this.travel?.item === item) this.stopTravel();
        item.classList.remove('is-open');
        item.querySelector(':scope > .kvs-nav__link').setAttribute('aria-expanded', 'false');
      };
      if (delay) this.timers.set(item, setTimeout(run, delay));
      else run();
    }

    closeAll(returnFocus = false) {
      const openItem = this.items.find((item) => item.classList.contains('is-open'));
      this.items.forEach((item) => this.close(item));
      if (returnFocus && openItem && openItem.contains(document.activeElement)) {
        this.suppressFocusOpen = true;
        openItem.querySelector(':scope > .kvs-nav__link').focus();
        this.suppressFocusOpen = false;
      }
    }
  }

  customElements.define('kvs-nav', KvsNav);
}

if (!customElements.get('kvs-announcements')) {
  class KvsAnnouncements extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.kvs-strip__slide'));
      this.index = Math.max(
        0,
        this.slides.findIndex((slide) => slide.classList.contains('is-active')),
      );
      this.paused = false;

      this.addEventListener('click', (event) => {
        const step = event.target.closest('[data-kvs-announcement-step]');
        if (step) this.show(this.index + Number(step.dataset.kvsAnnouncementStep));
      });

      const seconds = Number(this.dataset.speed) || 5;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (this.slides.length < 2 || this.dataset.autoplay !== 'true' || reduceMotion) return;

      this.addEventListener('pointerenter', () => (this.paused = true));
      this.addEventListener('pointerleave', () => (this.paused = false));
      this.addEventListener('focusin', () => (this.paused = true));
      this.addEventListener('focusout', () => (this.paused = false));

      this.timer = setInterval(() => {
        if (!this.paused && !document.hidden) this.show(this.index + 1);
      }, seconds * 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    show(next) {
      const target = (next + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        const active = i === target;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      this.index = target;
    }
  }

  customElements.define('kvs-announcements', KvsAnnouncements);
}
